/**
 * Cellavent Laborwert-Rechner — nativer Ersatz fuer die Next.js-LabApp.
 * Keine externen Abhaengigkeiten. Daten kommen aus cv-labwerte-data.js.
 *
 * Fachdaten (Grenzwerte, Texte) stammen unveraendert aus der bisherigen App.
 */
(function () {
  'use strict';

  var LOW_TYPES = ['TOO_LOW', 'LOW'];
  var HIGH_TYPES = ['HIGH', 'EXTREME_HIGH'];
  var STORAGE_KEY = 'cv-labwerte-v1';

  /* ---------------------------------------------------------------- Utils */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function num(v) {
    if (typeof v === 'number') return v;
    if (v == null) return NaN;
    // Deutsche Eingabe mit Komma zulassen - die alte App verlangte US-Schreibweise.
    return parseFloat(String(v).trim().replace(/\s/g, '').replace(',', '.'));
  }
  function fmt(n) {
    if (!isFinite(n)) return '–';
    var a = Math.abs(n);
    var d = a >= 1000 ? 0 : a >= 100 ? 1 : a >= 1 ? 2 : 3;
    return n.toFixed(d).replace(/\.?0+$/, '').replace('.', ',');
  }
  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function deDate(iso) {
    var p = String(iso).split('-');
    return p.length === 3 ? p[2] + '.' + p[1] + '.' + p[0] : iso;
  }

  /* ------------------------------------------------------------ Fachlogik */

  /** Findet das Grenzwert-Objekt fuer Geschlecht, Einheit und Alter. */
  function findBorders(value, gender, unit, age) {
    var idx = value.units.indexOf(unit);
    if (idx < 0) idx = 0;
    var arr = (gender === 'FEMALE' ? value.borders.women : value.borders.men)[idx];
    if (!arr) return null;
    var hit = null;
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].fromAge <= age && age <= arr[i].toAge) hit = arr[i];
    }
    return hit || arr[0] || null;
  }

  /** Ergebnis-Kategorie fuer einen Messwert. Portiert aus service/data.js. */
  function classify(value, gender, unit, age, v) {
    var b = findBorders(value, gender, unit, age);
    if (!b || !isFinite(v)) return { type: 'NONE', borders: b };
    var type = 'NONE';
    Object.keys(b).forEach(function (key) {
      var r = b[key];
      if (Array.isArray(r) && r[0] <= v && v <= r[1]) type = key;
    });
    // Die Originaldaten decken nach oben nur bis zu einer festen Schranke ab
    // (bei manchen Werten z.B. 2.000.000). Liegt die Eingabe darueber, faellt
    // sie sonst als 'NONE' durch. Das ist fast immer ein Tippfehler oder eine
    // falsche Einheit - dafuer gibt es eine eigene, verstaendliche Kategorie.
    if (type === 'NONE') {
      var hi = -Infinity, lo = Infinity;
      Object.keys(b).forEach(function (key) {
        if (Array.isArray(b[key])) { hi = Math.max(hi, b[key][1]); lo = Math.min(lo, b[key][0]); }
      });
      if (isFinite(hi) && v > hi) type = 'OUT_OF_RANGE';
      else if (isFinite(lo) && v < lo) type = 'OUT_OF_RANGE';
    }
    return { type: type, borders: b };
  }

  /** Normbereich als [min,max] aus einem Grenzwert-Objekt. */
  function normalRange(b) {
    return b && Array.isArray(b.NORMAL) ? b.NORMAL.slice() : null;
  }

  /**
   * Skala fuer die Balkenanzeige. EXTREME_HIGH reicht in den Daten teils bis
   * 2.000.000 - das ist als Achse unbrauchbar. Deshalb wird um den Normbereich
   * herum skaliert und alles darueber hinaus am Rand markiert.
   */
  function scaleFor(b) {
    var n = normalRange(b);
    if (!n) return null;
    var w = n[1] - n[0];
    if (!(w > 0)) w = Math.max(Math.abs(n[1]) * 0.2, 1);
    return { min: Math.max(0, n[0] - w), max: n[1] + w, normal: n };
  }

  function pct(x, s) {
    return Math.max(0, Math.min(100, ((x - s.min) / (s.max - s.min)) * 100));
  }

  /** Prueft die Musterregeln gegen die klassifizierten Ergebnisse. */
  function matchPatterns(patterns, results) {
    var byId = {};
    results.forEach(function (r) { byId[r.value.id] = r.type; });
    var hits = [];
    patterns.forEach(function (p) {
      var matched = [];
      p.need.forEach(function (pair) {
        var t = byId[pair[0]];
        if (t && pair[1].indexOf(t) >= 0) matched.push(pair[0]);
      });
      if (matched.length < (p.min || p.need.length)) return;
      if (p.also) {
        var ok = p.also.every(function (pair) {
          var t = byId[pair[0]];
          return t == null || pair[1].indexOf(t) >= 0;
        });
        if (!ok) return;
      }
      hits.push({ pattern: p, ids: matched });
    });
    return hits;
  }

  /* ------------------------------------------------------------------ App */

  function init(root) {
    var DATA = window.__CV_LAB__;
    if (!DATA || !root) return;

    var byId = {};
    DATA.values.forEach(function (v) { byId[v.id] = v; });

    var cfg = {
      productUrl: root.dataset.productUrl || '',
      productLabel: root.dataset.productLabel || '',
      productText: root.dataset.productText || '',
      disclaimer: root.dataset.disclaimer || '',
      enableStorage: root.dataset.storage === 'true',
      enablePatterns: root.dataset.patterns === 'true',
      enablePrint: root.dataset.print === 'true'
    };

    var state = {
      step: 1,
      age: 40,
      gender: 'FEMALE',
      picked: [],           // [{id, unit, raw}]
      results: null,
      compareWith: null
    };

    /* ---------- Persistenz (nur lokal im Browser) ---------- */
    function loadStore() {
      if (!cfg.enableStorage) return [];
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch (e) { return []; }
    }
    function saveStore(list) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-12))); } catch (e) { /* Speicher voll oder blockiert */ }
    }
    function saveCurrent() {
      var list = loadStore();
      list.push({
        date: todayISO(),
        age: state.age,
        gender: state.gender,
        picked: state.picked.map(function (p) { return { id: p.id, unit: p.unit, raw: p.raw }; })
      });
      saveStore(list);
      return list;
    }

    /* ---------- Berechnung ---------- */
    function compute() {
      var out = [];
      state.picked.forEach(function (p) {
        var v = byId[p.id];
        if (!v) return;
        var n = num(p.raw);
        if (!isFinite(n)) return;
        var c = classify(v, state.gender, p.unit, state.age, n);
        out.push({ value: v, unit: p.unit, num: n, type: c.type, borders: c.borders });
      });
      return out;
    }

    /* ---------- Rendering ---------- */

    // Eine einzige, dauerhafte Live-Region. Wird sie bei jedem Render neu
    // erzeugt, kuendigen Screenreader die Aenderung oft gar nicht an.
    var liveRegion = el('div', 'cv-lab__sr');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('role', 'status');

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function render(opts) {
      var moveFocus = opts && opts.focus;
      root.innerHTML = '';
      root.appendChild(liveRegion);
      root.appendChild(renderProgress());
      var panel;
      if (state.step === 1) panel = renderStepProfile();
      else if (state.step === 2) panel = renderStepPick();
      else if (state.step === 3) panel = renderStepInput();
      else panel = renderStepResult();
      root.appendChild(panel);

      var titles = ['Profil', 'Werte wählen', 'Werte eintragen', 'Ergebnis'];
      liveRegion.textContent = 'Schritt ' + state.step + ' von 4: ' + titles[state.step - 1];

      // Beim Schrittwechsel den Fokus in die neue Ueberschrift setzen, damit
      // Tastatur- und Screenreader-Nutzer nicht am Seitenanfang stranden.
      if (moveFocus) {
        var h = panel.querySelector('.cv-lab__h');
        if (h) {
          h.setAttribute('tabindex', '-1');
          try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
          h.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
        }
      }
    }

    function goTo(step) {
      state.step = step;
      clearMessage();
      render({ focus: true });
    }

    function renderProgress() {
      var steps = ['Profil', 'Werte wählen', 'Werte eintragen', 'Ergebnis'];
      var nav = el('ol', 'cv-lab__steps');
      nav.setAttribute('aria-label', 'Fortschritt');
      steps.forEach(function (label, i) {
        var n = i + 1;
        var done = n < state.step;
        var li = el('li', 'cv-lab__step' + (n === state.step ? ' is-current' : '') + (done ? ' is-done' : ''));
        if (n === state.step) li.setAttribute('aria-current', 'step');
        var b = el('button', 'cv-lab__step-btn');
        b.type = 'button';
        b.disabled = n > state.step;
        b.innerHTML = '<span class="cv-lab__step-num" aria-hidden="true"><span>' + n + '</span></span>' +
          '<span class="cv-lab__step-label">' + esc(label) + '</span>';
        b.setAttribute('aria-label', 'Schritt ' + n + ': ' + label + (done ? ' (abgeschlossen)' : ''));
        b.addEventListener('click', function () { if (n < state.step) goTo(n); });
        li.appendChild(b);
        nav.appendChild(li);
      });
      return nav;
    }

    /* ---------- Inline-Meldungen statt Browser-Dialogen ---------- */

    function clearMessage() {
      var old = root.querySelector('.cv-lab__msg');
      if (old) old.remove();
    }
    function showMessage(text, anchor) {
      clearMessage();
      var m = el('p', 'cv-lab__msg', text);
      m.setAttribute('role', 'alert');
      m.setAttribute('tabindex', '-1');
      (anchor || root).appendChild(m);
      try { m.focus({ preventScroll: true }); } catch (e) {}
      m.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }

    /* --- Schritt 1: Profil --- */
    function renderStepProfile() {
      var box = el('div', 'cv-lab__panel');
      box.appendChild(el('h3', 'cv-lab__h', 'Zu wem gehören die Werte?'));
      box.appendChild(el('p', 'cv-lab__lead', 'Referenzbereiche unterscheiden sich nach Alter und biologischem Geschlecht. Beides brauchen wir, um deine Werte richtig einzuordnen.'));

      // Alter
      var fAge = el('div', 'cv-lab__field');
      var lAge = el('label', 'cv-lab__label', 'Alter');
      lAge.setAttribute('for', 'cvlab-age');
      fAge.appendChild(lAge);
      var row = el('div', 'cv-lab__age-row');
      var slider = el('input', 'cv-lab__range');
      slider.type = 'range'; slider.min = 18; slider.max = 110; slider.step = 1; slider.value = state.age;
      slider.id = 'cvlab-age';
      slider.setAttribute('aria-describedby', 'cvlab-age-out');
      var outp = el('output', 'cv-lab__age-out');
      outp.id = 'cvlab-age-out';
      outp.textContent = state.age + ' Jahre';
      slider.addEventListener('input', function () {
        state.age = parseInt(slider.value, 10);
        outp.textContent = state.age + ' Jahre';
      });
      row.appendChild(slider); row.appendChild(outp);
      fAge.appendChild(row);
      var hint = el('p', 'cv-lab__hint', 'Die hinterlegten Referenzbereiche gelten für Erwachsene ab 18 Jahren.');
      fAge.appendChild(hint);
      box.appendChild(fAge);

      // Geschlecht
      var fG = el('fieldset', 'cv-lab__field cv-lab__fieldset');
      fG.appendChild(el('legend', 'cv-lab__label', 'Biologisches Geschlecht'));
      var gr = el('div', 'cv-lab__choice');
      [['FEMALE', 'weiblich'], ['MALE', 'männlich']].forEach(function (g) {
        var id = 'cvlab-g-' + g[0];
        var wrap = el('div', 'cv-lab__choice-item');
        var inp = el('input');
        inp.type = 'radio'; inp.name = 'cvlab-gender'; inp.id = id; inp.value = g[0];
        inp.checked = state.gender === g[0];
        inp.addEventListener('change', function () { state.gender = g[0]; });
        var lab = el('label', 'cv-lab__choice-label', g[1]);
        lab.setAttribute('for', id);
        wrap.appendChild(inp); wrap.appendChild(lab);
        gr.appendChild(wrap);
      });
      fG.appendChild(gr);
      fG.appendChild(el('p', 'cv-lab__hint', 'Gemeint ist das Geschlecht, auf das sich die Referenzbereiche in deinem Laborbefund beziehen.'));
      box.appendChild(fG);

      box.appendChild(actions([{ label: 'Weiter', primary: true, on: function () { goTo(2); } }], true));

      // Gespeicherte Messungen
      var store = loadStore();
      if (cfg.enableStorage && store.length) {
        var s = el('div', 'cv-lab__saved');
        s.appendChild(el('h4', 'cv-lab__h4', 'Gespeicherte Messungen'));
        var ul = el('ul', 'cv-lab__saved-list');
        store.slice().reverse().forEach(function (entry, i) {
          var li = el('li', 'cv-lab__saved-item');
          li.appendChild(el('span', 'cv-lab__saved-date', deDate(entry.date) + ' · ' + entry.picked.length + ' Werte'));
          var b = el('button', 'cv-lab__link');
          b.type = 'button'; b.textContent = 'Laden';
          b.addEventListener('click', function () {
            state.age = entry.age; state.gender = entry.gender;
            state.picked = entry.picked.map(function (p) { return { id: p.id, unit: p.unit, raw: p.raw }; });
            goTo(3);
          });
          li.appendChild(b);
          ul.appendChild(li);
        });
        s.appendChild(ul);
        var clear = el('button', 'cv-lab__link cv-lab__link--muted');
        clear.type = 'button'; clear.textContent = 'Alle gespeicherten Messungen löschen';
        clear.addEventListener('click', function () {
          if (s.querySelector('.cv-lab__confirm')) return;
          var box2 = el('div', 'cv-lab__confirm');
          box2.appendChild(el('p', null, 'Alle lokal gespeicherten Messungen löschen? Das lässt sich nicht rückgängig machen.'));
          var row = el('div', 'cv-lab__confirm-actions');
          var yes = el('button', 'cv-lab__btn cv-lab__btn--primary');
          yes.type = 'button'; yes.textContent = 'Ja, löschen';
          yes.addEventListener('click', function () { saveStore([]); render({ focus: true }); });
          var no = el('button', 'cv-lab__btn');
          no.type = 'button'; no.textContent = 'Abbrechen';
          no.addEventListener('click', function () { box2.remove(); clear.focus(); });
          row.appendChild(yes); row.appendChild(no);
          box2.appendChild(row);
          clear.insertAdjacentElement('afterend', box2);
          yes.focus();
        });
        s.appendChild(clear);
        s.appendChild(el('p', 'cv-lab__hint', 'Diese Daten liegen ausschließlich in deinem Browser. Sie werden nicht übertragen und nicht auf einem Server gespeichert.'));
        box.appendChild(s);
      }
      return box;
    }

    /* --- Schritt 2: Werte waehlen --- */
    function renderStepPick() {
      var box = el('div', 'cv-lab__panel');
      box.appendChild(el('h3', 'cv-lab__h', 'Welche Werte möchtest du einordnen?'));
      box.appendChild(el('p', 'cv-lab__lead', 'Wähle ein komplettes Panel oder einzelne Werte. Du kannst jederzeit weitere ergänzen.'));

      // Panels
      var pw = el('div', 'cv-lab__panels');
      DATA.panels.forEach(function (p) {
        var b = el('button', 'cv-lab__chip');
        b.type = 'button';
        var have = p.ids.filter(function (id) { return isPicked(id); }).length;
        var all = have === p.ids.length;
        if (all) b.classList.add('is-on');
        b.innerHTML = '<span class="cv-lab__chip-name">' + esc(p.name) + '</span>' +
          '<span class="cv-lab__chip-hint">' + esc(p.hint) + ' · ' + p.ids.length + ' Werte</span>';
        b.setAttribute('aria-pressed', all ? 'true' : 'false');
        b.addEventListener('click', function () {
          if (all) p.ids.forEach(unpick); else p.ids.forEach(pick);
          render();
        });
        pw.appendChild(b);
      });
      box.appendChild(pw);

      // Suche
      var sf = el('div', 'cv-lab__field');
      var sl = el('label', 'cv-lab__label', 'Einzelnen Wert suchen');
      sl.setAttribute('for', 'cvlab-search');
      sf.appendChild(sl);
      var si = el('input', 'cv-lab__input');
      si.type = 'search'; si.id = 'cvlab-search'; si.placeholder = 'z. B. Hämoglobin, GPT, Kalium';
      si.setAttribute('autocomplete', 'off');
      sf.appendChild(si);
      box.appendChild(sf);

      var listWrap = el('div', 'cv-lab__list');
      listWrap.setAttribute('role', 'group');
      listWrap.setAttribute('aria-label', 'Verfügbare Laborwerte');
      box.appendChild(listWrap);

      function drawList() {
        listWrap.innerHTML = '';
        var q = si.value.trim().toLowerCase();
        var groups = {};
        DATA.values.forEach(function (v) {
          if (q && v.name.toLowerCase().indexOf(q) < 0) return;
          (groups[v.cat] = groups[v.cat] || []).push(v);
        });
        var keys = Object.keys(groups);
        if (!keys.length) {
          listWrap.appendChild(el('p', 'cv-lab__hint', 'Kein Wert gefunden. Vielleicht heißt er im Befund anders — versuch eine Abkürzung wie „GPT" oder „MCV".'));
          return;
        }
        keys.forEach(function (cat) {
          listWrap.appendChild(el('h4', 'cv-lab__h4', cat));
          var ul = el('div', 'cv-lab__opts');
          groups[cat].forEach(function (v) {
            var id = 'cvlab-v-' + v.id;
            var item = el('div', 'cv-lab__opt');
            var cb = el('input');
            cb.type = 'checkbox'; cb.id = id; cb.checked = isPicked(v.id);
            cb.addEventListener('change', function () { cb.checked ? pick(v.id) : unpick(v.id); updateCount(); });
            var lab = el('label', 'cv-lab__opt-label', v.name);
            lab.setAttribute('for', id);
            item.appendChild(cb); item.appendChild(lab);
            ul.appendChild(item);
          });
          listWrap.appendChild(ul);
        });
      }
      si.addEventListener('input', drawList);
      drawList();

      var countEl = el('p', 'cv-lab__count');
      function updateCount() {
        countEl.textContent = state.picked.length === 0 ? 'Noch kein Wert ausgewählt.'
          : state.picked.length + (state.picked.length === 1 ? ' Wert ausgewählt.' : ' Werte ausgewählt.');
        nextBtn.disabled = state.picked.length === 0;
      }
      box.appendChild(countEl);

      var acts = actions([
        { label: 'Zurück', on: function () { goTo(1); } },
        { label: 'Weiter', primary: true, on: function () { goTo(3); } }
      ], true);
      var nextBtn = acts.querySelector('.cv-lab__btn--primary');
      box.appendChild(acts);
      updateCount();
      return box;
    }

    function isPicked(id) { return state.picked.some(function (p) { return p.id === id; }); }
    function pick(id) {
      if (isPicked(id)) return;
      var v = byId[id];
      if (v) state.picked.push({ id: id, unit: v.defaultUnit, raw: '' });
    }
    function unpick(id) {
      state.picked = state.picked.filter(function (p) { return p.id !== id; });
    }

    /* --- Schritt 3: Werte eintragen --- */
    function renderStepInput() {
      var box = el('div', 'cv-lab__panel');
      box.appendChild(el('h3', 'cv-lab__h', 'Trag deine Werte ein'));
      box.appendChild(el('p', 'cv-lab__lead', 'Kommazahlen kannst du mit Komma oder Punkt eingeben. Werte, die du leer lässt, werden übersprungen.'));

      var form = el('div', 'cv-lab__inputs');
      state.picked.forEach(function (p) {
        var v = byId[p.id];
        if (!v) return;
        var row = el('div', 'cv-lab__inrow');
        var head = el('div', 'cv-lab__inrow-head');
        var lab = el('label', 'cv-lab__inlabel', v.name);
        lab.setAttribute('for', 'cvlab-in-' + v.id);
        head.appendChild(lab);
        var rm = el('button', 'cv-lab__remove');
        rm.type = 'button';
        rm.setAttribute('aria-label', v.name + ' entfernen');
        rm.innerHTML = '&times;';
        rm.addEventListener('click', function () { unpick(v.id); render(); });
        head.appendChild(rm);
        row.appendChild(head);

        var ctrl = el('div', 'cv-lab__inctrl');
        var inp = el('input', 'cv-lab__input cv-lab__input--num');
        inp.type = 'text';
        inp.inputMode = 'decimal';
        inp.id = 'cvlab-in-' + v.id;
        inp.value = p.raw;
        inp.placeholder = 'Wert';
        inp.setAttribute('enterkeyhint', 'next');
        inp.setAttribute('autocomplete', 'off');
        inp.addEventListener('input', function () { p.raw = inp.value; refreshPreview(); });
        // Enter springt ins naechste Feld statt das Formular zu schliessen.
        inp.addEventListener('keydown', function (ev) {
          if (ev.key !== 'Enter') return;
          ev.preventDefault();
          var all = Array.prototype.slice.call(form.querySelectorAll('.cv-lab__input--num'));
          var i = all.indexOf(inp);
          if (i > -1 && i + 1 < all.length) all[i + 1].focus();
          else inp.blur();
        });
        ctrl.appendChild(inp);

        if (v.units.length > 1) {
          var sel = el('select', 'cv-lab__select');
          sel.setAttribute('aria-label', 'Einheit für ' + v.name);
          v.units.forEach(function (u) {
            var o = el('option', null, u);
            o.value = u;
            if (u === p.unit) o.selected = true;
            sel.appendChild(o);
          });
          sel.addEventListener('change', function () { p.unit = sel.value; refreshPreview(); });
          ctrl.appendChild(sel);
        } else {
          ctrl.appendChild(el('span', 'cv-lab__unit', v.units[0]));
        }
        row.appendChild(ctrl);

        var prev = el('p', 'cv-lab__preview');
        prev.dataset.for = String(v.id);
        row.appendChild(prev);
        form.appendChild(row);
      });
      box.appendChild(form);

      function refreshPreview() {
        state.picked.forEach(function (p) {
          var v = byId[p.id];
          var node = form.querySelector('.cv-lab__preview[data-for="' + p.id + '"]');
          if (!node || !v) return;
          var b = findBorders(v, state.gender, p.unit, state.age);
          var n = normalRange(b);
          var val = num(p.raw);
          if (!n) { node.textContent = ''; return; }
          var range = 'Normbereich ' + fmt(n[0]) + '–' + fmt(n[1]) + ' ' + p.unit;
          if (!isFinite(val)) { node.textContent = range; node.className = 'cv-lab__preview'; return; }
          var t = classify(v, state.gender, p.unit, state.age, val).type;
          node.textContent = range;
          node.className = 'cv-lab__preview is-' + tone(t);
        });
      }
      refreshPreview();

      box.appendChild(actions([
        { label: 'Zurück', on: function () { goTo(2); } },
        {
          label: 'Ergebnis anzeigen', primary: true, on: function () {
            state.results = compute();
            if (!state.results.length) {
              showMessage('Bitte trag mindestens einen Wert ein — sonst gibt es nichts einzuordnen.', box);
              var first = box.querySelector('.cv-lab__input--num');
              if (first) first.focus();
              return;
            }
            goTo(4);
          }
        }
      ], true));
      return box;
    }

    /* --- Schritt 4: Ergebnis --- */
    function tone(t) {
      if (t === 'NORMAL') return 'ok';
      if (t === 'EXTREME_HIGH') return 'alert';
      if (LOW_TYPES.indexOf(t) >= 0 || HIGH_TYPES.indexOf(t) >= 0) return 'warn';
      return 'none';
    }
    function toneLabel(t) {
      return {
        NORMAL: 'Im Normbereich',
        LOW: 'Leicht erniedrigt',
        TOO_LOW: 'Erniedrigt',
        HIGH: 'Erhöht',
        EXTREME_HIGH: 'Stark erhöht',
        OUT_OF_RANGE: 'Bitte prüfen'
      }[t] || 'Nicht einzuordnen';
    }
    function noteFor(t) {
      if (t === 'OUT_OF_RANGE') {
        return 'Dieser Wert liegt weit außerhalb des Bereichs, den wir einordnen können. Bitte prüf, ob die Einheit richtig gewählt ist und ob sich ein Tippfehler eingeschlichen hat.';
      }
      if (t === 'NONE') {
        return 'Für diese Kombination aus Alter, Geschlecht und Einheit liegt uns kein Referenzbereich vor.';
      }
      return null;
    }

    function renderStepResult() {
      var box = el('div', 'cv-lab__panel cv-lab__result');
      var res = state.results || [];

      var head = el('div', 'cv-lab__result-head');
      head.appendChild(el('h3', 'cv-lab__h', 'Dein Ergebnis'));
      head.appendChild(el('p', 'cv-lab__lead',
        res.length + (res.length === 1 ? ' Wert' : ' Werte') + ' · ' + state.age + ' Jahre · ' +
        (state.gender === 'FEMALE' ? 'weiblich' : 'männlich') + ' · ' + deDate(todayISO())));
      box.appendChild(head);

      // Zusammenfassung
      var counts = { ok: 0, warn: 0, alert: 0, none: 0 };
      res.forEach(function (r) { counts[tone(r.type)]++; });
      var sum = el('div', 'cv-lab__summary');
      [['ok', counts.ok, 'im Normbereich'], ['warn', counts.warn, 'außerhalb des Normbereichs'], ['alert', counts.alert, 'deutlich auffällig']].forEach(function (c) {
        if (!c[1]) return;
        var d = el('div', 'cv-lab__sumcard is-' + c[0]);
        d.appendChild(el('span', 'cv-lab__sumnum', String(c[1])));
        d.appendChild(el('span', 'cv-lab__sumlab', c[2]));
        sum.appendChild(d);
      });
      box.appendChild(sum);

      // Muster
      if (cfg.enablePatterns) {
        var hits = matchPatterns(DATA.patterns, res);
        if (hits.length) {
          var pw = el('section', 'cv-lab__patterns');
          pw.appendChild(el('h4', 'cv-lab__h4', 'Auffällige Konstellationen'));
          pw.appendChild(el('p', 'cv-lab__hint', 'Diese Hinweise entstehen aus dem Zusammenspiel mehrerer Werte. Sie sind keine Diagnose — ob und was daraus folgt, kann nur ärztlich beurteilt werden.'));
          hits.forEach(function (h) {
            var d = el('details', 'cv-lab__pattern');
            var s = el('summary', 'cv-lab__pattern-sum');
            s.innerHTML = '<span class="cv-lab__pattern-name">' + esc(h.pattern.name) + '</span>' +
              '<span class="cv-lab__pattern-vals">' + esc(h.ids.map(function (i) { return byId[i].name; }).join(' · ')) + '</span>';
            d.appendChild(s);
            d.appendChild(el('p', 'cv-lab__pattern-text', h.pattern.text));
            pw.appendChild(d);
          });
          box.appendChild(pw);
        }
      }

      // Einzelwerte
      var list = el('div', 'cv-lab__cards');
      res.forEach(function (r) { list.appendChild(renderCard(r)); });
      box.appendChild(list);

      // Disclaimer
      if (cfg.disclaimer) {
        var dc = el('aside', 'cv-lab__disclaimer');
        dc.setAttribute('role', 'note');
        dc.innerHTML = cfg.disclaimer;
        box.appendChild(dc);
      }

      // Produkt-Hinweis
      if (cfg.productUrl && cfg.productLabel) {
        var pr = el('aside', 'cv-lab__product');
        if (cfg.productText) pr.appendChild(el('p', 'cv-lab__product-text', cfg.productText));
        var a = el('a', 'cv-lab__btn cv-lab__btn--primary', cfg.productLabel);
        a.href = cfg.productUrl;
        pr.appendChild(a);
        box.appendChild(pr);
      }

      // Aktionen
      var acts = el('div', 'cv-lab__actions cv-lab__actions--result');
      var back = el('button', 'cv-lab__btn');
      back.type = 'button'; back.textContent = 'Werte ändern';
      back.addEventListener('click', function () { goTo(3); });
      acts.appendChild(back);

      if (cfg.enableStorage) {
        var save = el('button', 'cv-lab__btn');
        save.type = 'button'; save.textContent = 'Ergebnis speichern';
        save.addEventListener('click', function () {
          saveCurrent();
          save.textContent = 'Gespeichert ✓';
          save.disabled = true;
        });
        acts.appendChild(save);
      }
      if (cfg.enablePrint) {
        var pr2 = el('button', 'cv-lab__btn');
        pr2.type = 'button'; pr2.textContent = 'Drucken / als PDF sichern';
        pr2.addEventListener('click', function () { window.print(); });
        acts.appendChild(pr2);
      }
      var again = el('button', 'cv-lab__btn cv-lab__btn--ghost');
      again.type = 'button'; again.textContent = 'Neu beginnen';
      again.addEventListener('click', function () {
        state.picked = []; state.results = null; goTo(1);
      });
      acts.appendChild(again);
      box.appendChild(acts);

      // Vergleich mit gespeicherter Messung
      if (cfg.enableStorage) {
        var store = loadStore();
        if (store.length) box.appendChild(renderCompare(store, res));
      }
      return box;
    }

    function renderCard(r) {
      var v = r.value;
      var card = el('article', 'cv-lab__card is-' + tone(r.type));
      var h = el('header', 'cv-lab__card-head');
      h.appendChild(el('h4', 'cv-lab__card-title', v.name));
      var badge = el('span', 'cv-lab__badge is-' + tone(r.type), toneLabel(r.type));
      h.appendChild(badge);
      card.appendChild(h);

      var meas = el('p', 'cv-lab__meas');
      meas.innerHTML = '<strong>' + esc(fmt(r.num)) + '</strong> <span>' + esc(r.unit) + '</span>';
      card.appendChild(meas);

      // Balken
      var s = scaleFor(r.borders);
      if (s) {
        var bar = el('div', 'cv-lab__bar');
        bar.setAttribute('role', 'img');
        bar.setAttribute('aria-label',
          'Dein Wert ' + fmt(r.num) + ' ' + r.unit + '. Normbereich ' + fmt(s.normal[0]) + ' bis ' + fmt(s.normal[1]) + ' ' + r.unit + '.');
        var zone = el('div', 'cv-lab__bar-normal');
        zone.style.left = pct(s.normal[0], s) + '%';
        zone.style.width = Math.max(2, pct(s.normal[1], s) - pct(s.normal[0], s)) + '%';
        bar.appendChild(zone);
        var marker = el('div', 'cv-lab__bar-mark is-' + tone(r.type));
        var below = r.num < s.min, above = r.num > s.max;
        marker.style.left = pct(r.num, s) + '%';
        if (below || above) marker.classList.add('is-off');
        bar.appendChild(marker);
        card.appendChild(bar);

        var sc = el('div', 'cv-lab__bar-scale');
        sc.appendChild(el('span', null, (below ? '≤ ' : '') + fmt(s.min)));
        sc.appendChild(el('span', 'cv-lab__bar-normlab', 'Normbereich ' + fmt(s.normal[0]) + '–' + fmt(s.normal[1])));
        sc.appendChild(el('span', null, (above ? '≥ ' : '') + fmt(s.max)));
        card.appendChild(sc);
      }

      var note = noteFor(r.type);
      if (note) card.appendChild(el('p', 'cv-lab__card-headline', note));

      var headline = (v.headline || {})[r.type];
      if (headline) card.appendChild(el('p', 'cv-lab__card-headline', headline.trim()));

      var txt = (v.text || {})[r.type];
      if (txt) {
        var d = el('details', 'cv-lab__more');
        d.appendChild(el('summary', 'cv-lab__more-sum', 'Was bedeutet das?'));
        d.appendChild(el('p', 'cv-lab__more-text', txt));
        if (v.definition) {
          var d2 = el('p', 'cv-lab__more-def');
          d2.innerHTML = '<strong>Was ist ' + esc(v.name) + '?</strong> ' + esc(v.definition);
          d.appendChild(d2);
        }
        card.appendChild(d);
      }
      return card;
    }

    function renderCompare(store, res) {
      var wrap = el('section', 'cv-lab__compare');
      wrap.appendChild(el('h4', 'cv-lab__h4', 'Mit früherer Messung vergleichen'));
      var sel = el('select', 'cv-lab__select');
      sel.setAttribute('aria-label', 'Frühere Messung wählen');
      var o0 = el('option', null, 'Bitte wählen');
      o0.value = '';
      sel.appendChild(o0);
      store.forEach(function (e, i) {
        var o = el('option', null, deDate(e.date) + ' · ' + e.picked.length + ' Werte');
        o.value = String(i);
        sel.appendChild(o);
      });
      wrap.appendChild(sel);
      var out = el('div', 'cv-lab__compare-out');
      wrap.appendChild(out);

      sel.addEventListener('change', function () {
        out.innerHTML = '';
        if (sel.value === '') return;
        var prev = store[parseInt(sel.value, 10)];
        var map = {};
        prev.picked.forEach(function (p) { map[p.id] = p; });
        var rows = [];
        res.forEach(function (r) {
          var p = map[r.value.id];
          if (!p) return;
          var old = num(p.raw);
          if (!isFinite(old)) return;
          // Nur bei gleicher Einheit vergleichen - sonst waere die Differenz irrefuehrend.
          if (p.unit !== r.unit) {
            rows.push({ name: r.value.name, note: 'andere Einheit (' + p.unit + ') — nicht vergleichbar' });
            return;
          }
          var diff = r.num - old;
          rows.push({ name: r.value.name, old: old, now: r.num, unit: r.unit, diff: diff });
        });
        if (!rows.length) {
          out.appendChild(el('p', 'cv-lab__hint', 'Keine gemeinsamen Werte in beiden Messungen.'));
          return;
        }
        var tbl = el('table', 'cv-lab__table');
        var thead = el('thead');
        var trh = el('tr');
        ['Wert', deDate(prev.date), 'Jetzt', 'Veränderung'].forEach(function (t) {
          var th = el('th', null, t); th.scope = 'col'; trh.appendChild(th);
        });
        thead.appendChild(trh); tbl.appendChild(thead);
        var tb = el('tbody');
        rows.forEach(function (row) {
          var tr = el('tr');
          var th = el('th', null, row.name); th.scope = 'row'; tr.appendChild(th);
          if (row.note) {
            var td = el('td', 'cv-lab__hint', row.note);
            td.colSpan = 3; tr.appendChild(td);
          } else {
            tr.appendChild(el('td', null, fmt(row.old) + ' ' + row.unit));
            tr.appendChild(el('td', null, fmt(row.now) + ' ' + row.unit));
            var sign = row.diff > 0 ? '+' : '';
            var dtd = el('td', 'cv-lab__diff', Math.abs(row.diff) < 1e-9 ? 'unverändert' : sign + fmt(row.diff));
            tr.appendChild(dtd);
          }
          tb.appendChild(tr);
        });
        tbl.appendChild(tb);
        var scroller = el('div', 'cv-lab__table-wrap');
        scroller.appendChild(tbl);
        out.appendChild(scroller);
        out.appendChild(el('p', 'cv-lab__hint', 'Einzelne Schwankungen zwischen zwei Messungen sind normal und für sich genommen selten aussagekräftig.'));
      });
      return wrap;
    }

    function actions(items, sticky) {
      var w = el('div', 'cv-lab__actions' + (sticky ? ' cv-lab__actions--sticky' : ''));
      items.forEach(function (it) {
        var b = el('button', 'cv-lab__btn' + (it.primary ? ' cv-lab__btn--primary' : ''));
        b.type = 'button';
        b.textContent = it.label;
        b.addEventListener('click', it.on);
        w.appendChild(b);
      });
      return w;
    }

    render();
  }

  /* ------------------------------------------------------------ Bootstrap */

  function boot() {
    var nodes = document.querySelectorAll('[data-cv-lab]');
    if (!nodes.length) return;
    Array.prototype.forEach.call(nodes, function (n) {
      if (n.dataset.cvLabReady) return;
      n.dataset.cvLabReady = '1';
      init(n);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Shopify Theme-Editor: Section wurde neu geladen
  document.addEventListener('shopify:section:load', boot);

  // Fuer Tests
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { classify: classify, findBorders: findBorders, matchPatterns: matchPatterns, scaleFor: scaleFor, num: num, normalRange: normalRange };
  }
})();
